import { useState } from "react";

import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { MedicalDocumentItem } from "@/Documents/Models/Medical/MedicalDocumentItem";

interface IRevokeApprovalModalProps {
    /** The callback method invoked when the component closes. */
    onClose: () => void;

    /** The selected document. */
    document: MedicalDocumentItem;

    /** Callback methods invoked when component Revoke the approval. */
    onRevokeApproval?: () => Promise<void>;
}

/**
 * The component that renders a view to revoke an approval document.
 * @param {IRevokeApprovalModalProps} props The component properties.
 */

function RevokeApprovalModal(props: IRevokeApprovalModalProps): JSX.Element {
    const sharedItem = props.document.getSharedItem();
    const needToRevokeSharedItem = sharedItem
        && sharedItem.IncludeLinkedItem
        && [PortalSyncStatus.Pending, PortalSyncStatus.Viewed].contains(props.document.getGlobalPortalStatus());
    const { getText } = useTranslation();
    const [isRevokingApprovalDisabled, setIsRevokingApprovalDisabled] = useState<boolean>(false);

    async function confirmRevokeApproval(): Promise<void> {
        setIsRevokingApprovalDisabled(true);
        await props.onRevokeApproval?.();

        sessionStore.dispatch(
            LocalOperations.Documents.View_ConfirmUnapprove,
            {
                document: props.document,
                needToRevokeSharedItem: needToRevokeSharedItem,
                then: () => setIsRevokingApprovalDisabled(false)
            }
        );
    }

    function renderModalContent(): JSX.Element {
        return (
            <>
                {needToRevokeSharedItem && (
                    <div>
                        <label>{getText("DocumentSharedItemRevokeApprovalLabel")}</label>
                        <br />
                        <br />
                    </div>
                )}
                {!needToRevokeSharedItem &&
                    <strong className="red-text">{getText("DocumentNonSharedItemRevokeApprovalLabel")}</strong>
                }
                <strong className="red-text">{getText("GeneralRevokeDocumentApprovalMessageLabel")}</strong>
            </>
        );
    }

    function renderModalFooter(): JSX.Element {
        return (
            <div className="text-right">
                <button
                    className="btn btn-default push-right-5"
                    onClick={() => props.onClose()}
                >
                    {getText("CancelLabel")}
                </button>
                <button
                    disabled={isRevokingApprovalDisabled}
                    className="btn btn-danger"
                    data-cy="revoke-approval-revoke"
                    onClick={confirmRevokeApproval}
                >
                    {getText("ContinueLabel")}
                </button>
            </div>
        );
    }

    return (
        <Modal
            showX
            showOverlay
            onClose={() => props.onClose()}
            id="unapprovePopup"
            title={getText("WarningLabel")}
            children={renderModalContent()}
            footer={renderModalFooter()}
        />
    );
}

export { RevokeApprovalModal };
